import "./FaqPage.css";
import { useState, useEffect, useRef } from "react";
import { TitleSection } from "../formatting/CardSection";
import PageCard from "../formatting/PageCard";
import { ILLUSTRATIONS } from "../formatting/Illustration";
import FormGroup from "../forms/FormGroup";
import Accordion, { normalizedAccordionId } from "../shared/Accordion";
import {
  useDebouncedQuery,
  search,
  normalizeComponentTree,
} from "../shared/Filter";
import { FilterField } from "../shared/FilterField";
import { NoResultsPlaceholder } from "../shared/NoResultsPlaceholder";
import { EVENTS } from "../Tracking";
import StaticRoutes from "../static-pages/StaticRoutes";
import queryString from "query-string";
import { useDocumentTitle, TITLE_POSTFIX } from "../shared/DocumentTitle";
import Main from "../Main";
import { ExternalTextLink, TextLink } from "@teamscheme/text-link";
import { History } from "history";
import StaticScreen from "../formatting/StaticScreen";
import { useHistory } from "react-router-dom";

interface FaqSectionItem {
  top?: boolean;
  question: string;
  answer: JSX.Element | string;
}

interface FaqSection {
  title: string;
  items: FaqSectionItem[];
}

const sections: FaqSection[] = [
  {
    title: "Opprett en kjøpekontrakt",
    items: [
      {
        question:
          "Jeg har ikke registreringsnummer hvordan oppretter jeg en kontrakt?",
        answer: (
          <span>
            Du kan velge «Finner ikke kjøretøyet» å starte en kontrakt uten
            registreringsnummer. Du kan da velge å legge inn understellsnummeret
            om du har det tilgjengelig.
          </span>
        ),
      },
      {
        question:
          "Jeg får opp: «Fant ingen kjøretøy på regnummeret. Prøv igjen.» når jeg legger inn reg.nummeret",
        answer: (
          <span>
            Sjekk at du har tastet inn riktig registreringsnummer. Hvis du
            fortsatt får feilmeldingen kan du velge «Finner ikke kjøretøyet» å
            starte en kontrakt uten registreringsnummer. Der kan du legge in
            registreringsnummeret manuelt eller understellsnummeret.
          </span>
        ),
      },
      {
        question:
          "Jeg får opp: «Fant ikke treff på FINN-koden. Sjekk at koden er korrekt og at kjøretøyet selges privat» når jeg legger inn.",
        answer: (
          <span>
            For å kunne bruke FINN-koden må kjøretøyet være en personbil, og
            selges av en privatperson (ikke en forhandler). Skal du kjøpe eller
            selge en annen type kjøretøy må du taste inn reg.nummeret.
          </span>
        ),
      },
    ],
  },
  {
    title: "Fyll ut kontraktsopplysninger",
    items: [
      {
        top: true,
        question: "Hvor kommer preutfylt data fra?",
        answer: (
          <span>
            Vegvesenet:{" "}
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="https://www.vegvesen.no/kjoretoy/kjop-og-salg/kjoretoyopplysninger/sjekk-kjoretoyopplysninger"
            >
              https://www.vegvesen.no/kjoretoy/kjop-og-salg/kjoretoyopplysninger/sjekk-kjoretoyopplysninger
            </a>
          </span>
        ),
      },
      {
        question: "Hva skal jeg svare eller fylle ut i de ulike feltene?",
        answer: "Se hjelpetekstene ved å trykke på spørsmålstegnene",
      },
      {
        question: "Hvor henter dere omregistreringsavgiften fra?",
        answer: (
          <span>
            Vi regner ut basert på skatteetaten sin matrise for beregning av
            omregistreringsavgiften. Les mer her:{" "}
            <ExternalTextLink href="https://www.skatteetaten.no/person/avgifter/bil/eierskifte/">
              https://www.skatteetaten.no/person/avgifter/bil/eierskifte/
            </ExternalTextLink>
          </span>
        ),
      },
      {
        question: "Hvordan vet dere navnet og adressen min?",
        answer: (
          <span>
            Vi søker opp i offentlige registre. Har du skjult/jobbabonnement får
            vi ikke treff. Da må du fylle ut manuelt.
          </span>
        ),
      },
      {
        question: "Kan jeg endre det jeg har lagt inn?",
        answer: (
          <span>
            Ja, du kan gå tilbake ved å trykke «tilbake» i navigasjonsmenyen
            nederst på siden, eller øverst til venstre på enkelte sider. Dette
            er mulig inntil dere har signert. Forhåndsvisningen oppdateres
            automatisk når du gjør endringer.
          </span>
        ),
      },
      {
        question:
          "Jeg får beskjed om at jeg ikke har fullført min del av kontrakten.",
        answer: (
          <span>
            Det er noen felter som er obligatoriske som må fylles ut for å få
            signert. Klikk på "Gå tilbake og fyll ut resten". Disse feltene er
            obligatoriske: Km.stand, pris, overtakelsesdato, dine opplysninger
            (alle feltene).
          </span>
        ),
      },
      {
        question:
          "Hva betyr: «Venter på at kjøper skal fylle ut sin del av kontrakten slik at dere kan signere»?",
        answer: (
          <span>
            Motparten har fått en invitasjon til å fylle ut kontrakten med sine
            opplysninger på SMS eller e-post. Begge parter må fylle ut hver sin
            del av kontrakten før dere kan signere.
          </span>
        ),
      },
    ],
  },
  {
    title: "Inviter motpart",
    items: [
      {
        question:
          "Hva gjør jeg når motparten ikke mottar SMS eller e-post med invitasjon?",
        answer: (
          <span>
            Prøv på nytt sjekk at mottakers mobilnummer eller e-post er riktig.
            Klikk på «send på nytt». Tast inn mobilnummer eller e-postadressen
            og send invitasjon.
          </span>
        ),
      },
      {
        question: "Motparten får fortsatt ikke SMS/e-post.",
        answer: <span>Ta kontakt på chatten så hjelper vi dere.</span>,
      },
      {
        question:
          "Jeg har forsøkt å invitere motpart flere ganger og sendt mange invitasjoner med mobilnummeret. Nå funker det ikke lenger.",
        answer: (
          <span>
            Du kan maks sende 10 invitasjoner via SMS. Forsøk å sende invitasjon
            via e-post i stedet, eller ta kontakt på chatten så hjelper vi deg.
          </span>
        ),
      },
    ],
  },
  {
    title: "Signering",
    items: [
      {
        question: "Hva er InkSign?",
        answer: (
          <span>
            InkSign er den enkleste metoden for å signerer dokumentene. Partene
            bruker fingeren sin for å signere på smarttelefon eller musepekeren
            på MAC eller PC samtidig som du fyller ut fullt navn. Dette er en
            god løsning ved fysisktilstedeværelse av begge parter hvor man i
            tillegg kan gjøre en sjekk om signaturen matcher motparten sin ID
            som bankkort/pass.
          </span>
        ),
      },
      {
        top: true,
        question:
          "Jeg venter på motparten sin signatur. Kan jeg lukke nettleseren?",
        answer: (
          <span>
            Ja, du kan lukke nettleseren. For å finne tilbake til kontrakten
            bruker du lenken du fikk tilsendt på SMS eller e-post.
          </span>
        ),
      },
      {
        question:
          "Det står at jeg venter på signatur fra motparten. Hva gjør jeg?",
        answer: (
          <span>
            Du må be motparten signere. Han kan klikke seg inn via lenken han
            fikk på SMS.
          </span>
        ),
      },
      {
        question: "Vi har signert, hvor finner jeg kontrakten?",
        answer: (
          <span>
            Du kommer til en bekreftelsesside når begge har signert. Der kan du
            laste ned kontrakten. Du kan nå denne via lenken du fikk på SMS. Du
            får også en e-post med kontrakten vedlagt.
          </span>
        ),
      },
      {
        question: "Kan jeg skrive ut kontrakten?",
        answer: (
          <span>
            Ja, du kan skrive ut kontrakten fra forhåndsvisningen hvis dere
            heller vil signere på papir. Høyreklikk på bildet av forhåndsvisning
            av kontrakten og lagre bilde. Dette kan skrives ut og signeres.
          </span>
        ),
      },
      {
        top: true,
        question: "Er digital signatur juridisk bindenede?",
        answer: (
          <span>
            Ja, les mer under:{" "}
            <TextLink linkTo={StaticRoutes.digitalSigning.path}>
              Om digital signering
            </TextLink>
          </span>
        ),
      },
      {
        question:
          "Vi oppdaget en feil i kontrakten etter at en av oss har signert, hva gjør vi?",
        answer: (
          <span>
            Så lenge ikke begge har signert kontrakten kan dere avbryte
            signeringen for å åpne kontrakten for endringer igjen. Signaturen
            blir da forkastet og dere kan prøve igjen. Hvis begge har signert må
            dere lage en ny kontrakt og vise til at kontrakten dere først hadde
            signert er ugyldig og at den nye kontrakten er den gyldige.
          </span>
        ),
      },
      {
        question:
          "Motparten har ikke smarttelefon og kan ikke åpne linker på telefonen sin. Hvordan får man signert?",
        answer: (
          <span>
            Du kan sende invitasjonen på e-post til mottaker i stedet. For sende
            ny invitasjon trykker du på lenken «inviter på nytt» fra
            Forhåndsvisningen, og velg «endre mottaker». Da skal du kunne sende
            invitasjonen på nytt via e-post.
          </span>
        ),
      },
      {
        question: "Er det mulig å endre kontrakten etter den er signert?",
        answer: <span>Nei.</span>,
      },
    ],
  },
  {
    title: "Oppbevaring",
    items: [
      {
        top: true,
        question: "Hvor finner jeg kontrakten hos NAF?",
        answer: (
          <span>
            Den blir lagret og du kan finne tilbake til den via e-posten du fikk
            når kontrakten var signert eller lenken du fikk på SMS/E-post. Du
            kan via lenken velge å lagre kontrakten til Mitt NAF på{" "}
            <ExternalTextLink href="https://naf.no">naf.no</ExternalTextLink>.
            Det velger du selv på siden.
          </span>
        ),
      },
      {
        question:
          "Motparten fulgte ikke opp kontrakten - og nå ønsker jeg å slette kontrakten. Hva gjør jeg?",
        answer: (
          <span>
            Send reg.nummer på chat eller til{" "}
            <TextLink href="mailto:kundeservice@naf.no">
              kundeservice@naf.no
            </TextLink>
            . Hvis kontrakten bare er signert av én part kan vi slette
            kontrakten.
          </span>
        ),
      },
      {
        question:
          "Vi har signert en kontrakt, men må begynne på nytt for å legge til endringer. Hvordan sletter vi den signerte kontrakten?",
        answer: (
          <span>
            Det er ikke nødvendig at den signerte kontrakten slettes fra våre
            systemer. Dere kan likevel opprette en ny kontrakt og legge til et
            kort notat om at den første kontrakten ikke gjelder. Dette kan
            legges i feltet "Andre opplysninger". Forslag til tekst i en slik
            avtale kan være «Kjøpekontrakt for reg.nr av{" "}
            <strong>bilens reg.nr</strong> faller bort og erstattes av avtale av{" "}
            <strong>dato på ny kontrakt</strong>.».
          </span>
        ),
      },
      {
        question: "Handelen ble ikke noe av, hvordan sletter vi kontrakten?",
        answer: (
          <span>
            Dersom du ønsker at vi sletter en kontrakt før det har gått to år,
            kan du sende en e-post til  
            <TextLink href="mailto:kundeservice@naf.no">
              kundeservice@naf.no
            </TextLink>{" "}
            eller ta kontakt på chat. For å be om sletting av kontrakten må du
            være en av partene i kontrakten. Kontrakten blir sendt begge parter
            på e-post før den slettes fra våre systeme
          </span>
        ),
      },
    ],
  },
  {
    title: "Forsikring",
    items: [
      {
        question: "Hva er trafikkforsikringsavgift?",
        answer: (
          <span>
            1. januar 2018 ble årsavgiften erstattet med
            trafikkforsikringsavgift. Denne kreves inn av forsikringsselskapene.
            Du betaler kun for den tiden bilen er registrert med en forsikring.
            Du kan selv bestemme om du ønsker fakturaen: månedlig, kvartalsvis,
            halvår eller helår.{" "}
            <a
              href="https://naf.no/tips-og-rad/okonomi-og-avgifter/avgifter/dette-ma-du-vite-om-arsavgiften/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Les mer på naf.no
            </a>
          </span>
        ),
      },
    ],
  },
  {
    title: "Annet",
    items: [
      {
        question:
          "Kan kontrakten brukes hvis jeg selger eller kjøper fra et firma/næringsdrivende?",
        answer: (
          <span>
            Kjøpekontrakten vår er tilpasset kjøp mellom to privatpersoner. Den
            kan likevel brukes av næringsdrivende. Du kan krysse av for "firma"
            og fylle inn organisasjonsnummeret. Skal du også gjøre automatisk
            eierskifte må den som signerer ha signaturrett eller være prokurist
            for det aktuelle selskapet. Kontrakten kan ellers fylles ut som
            vanlig. Er en av partene en profesjonell bilforhandler bør det i
            stedet brukes en kontrakt som er godkjent av Norges
            Bilbransjeforbund (NBF).
          </span>
        ),
      },
    ],
  },
];

export const TopFaq = () => {
  const items = sections.map(({ items }) => items.filter((i) => i.top)).flat();

  const [openedId, setOpenedId] = useState(null);

  return (
    <div className="Faq">
      <Accordion className="">
        <section>
          {items.map((item, index) => (
            <Accordion.Row
              id={index}
              title={item.question}
              key={index}
              open={openedId === index}
              openEvent={{
                ...EVENTS.LANDING_PAGE.OPEN_QUESTION,
                label: item.question,
              }}
              onOpen={() => {
                if (openedId !== index) {
                  setOpenedId(null);
                }
              }}
            >
              <p>{item.answer}</p>
            </Accordion.Row>
          ))}
        </section>
      </Accordion>
      <section></section>
    </div>
  );
};

interface FaqProps {
  sections: FaqSection[];
  openAll?: boolean;
  openedId?: string;
  onOpen: (id: string) => void;
  history: History;
  query: string | null;
}

function Faq({
  sections,
  openAll,
  openedId: initialOpenedId,
  onOpen,
  history,
  query,
}: FaqProps) {
  const titles = useRef<HTMLHeadingElement[]>([]);

  useEffect(() => {
    const { hash } = history.location;

    if (hash) {
      const timeout = setTimeout(() => {
        const match = titles.current.filter((x) => `#${x.id}` === hash);
        if (match.length) {
          match[0].scrollIntoView();
        }
      });

      return () => clearTimeout(timeout);
    }
  }, [history]);

  const [openedId, setOpenedId] = useState<string | null | undefined>(
    initialOpenedId
  );

  useEffect(() => {
    if (query) setOpenedId(null);
  }, [query]);

  return (
    <div className="Faq">
      <Accordion>
        {sections.map((section, i) => (
          <section key={`section-${i}`}>
            <h3>{section.title}</h3>
            {section.items.map((item, j) => {
              const id = normalizedAccordionId(
                normalizeComponentTree(item.question)
              );
              return (
                <Accordion.Row
                  id={id}
                  title={item.question}
                  key={j}
                  open={openAll || openedId === id}
                  titleRef={(x) =>
                    titles.current[i * sections.length + j] === x
                  }
                  openEvent={{
                    ...EVENTS.FAQ.OPEN_QUESTION,
                    label: item.question,
                  }}
                  onOpen={() => {
                    if (openedId !== id) {
                      setOpenedId(null);
                      onOpen(id);
                    }
                  }}
                >
                  <p>{item.answer}</p>
                </Accordion.Row>
              );
            })}
          </section>
        ))}
      </Accordion>
    </div>
  );
}

const FaqPage = () => {
  const history = useHistory();
  const { query, clear, inputProps } = useDebouncedQuery({
    queryEvent: EVENTS.FAQ.FILTER,
    queryString:
      (history.location.search
        ? (queryString.parse(history.location.search).q as string)
        : null) || "",
  });

  const [matchedSections, setMatchedSections] = useState(sections);

  useEffect(() => {
    let newSections = !query
      ? sections
      : sections
          .map((section) => {
            const items = section.items
              .map((item) => {
                return {
                  ...item,
                  questionMatch: search(item.question, query as string),
                  answerMatch: search(item.answer, query),
                };
              })
              .filter(
                ({ questionMatch, answerMatch }) => questionMatch || answerMatch
              )
              .map(({ ...item }) => ({
                ...item,
                question: item.questionMatch || item.question,
                answer: item.answerMatch || item.answer,
              }));

            if (!items.length) return null;

            return {
              ...section,
              items,
            };
          })
          .filter((s) => !!s);
    setMatchedSections(newSections as FaqSection[]);
  }, [query]);

  const title = "Vanlige spørsmål";

  useDocumentTitle(`${title} ${TITLE_POSTFIX}`);

  return (
    <Main>
      <StaticScreen footer>
        <PageCard historyBack>
          <TitleSection title={title} illustration={ILLUSTRATIONS.HAPPY_FACES}>
            <FormGroup>
              <FilterField label="Søk på siden" inputProps={inputProps} />
            </FormGroup>
            <Faq
              query={query}
              sections={matchedSections}
              openAll={!!query}
              openedId={
                history.location.hash
                  ? history.location.hash.replace(/^#/, "")
                  : undefined
              }
              onOpen={(id) => {
                if (!query) {
                  window.history.replaceState(
                    null,
                    document.title,
                    document.location.hash.length
                      ? document.location.href.replace(
                          document.location.hash,
                          `#${id}`
                        )
                      : `${document.location.href}#${id}`
                  );
                }
              }}
              history={history}
            />
            {query && !matchedSections.length ? (
              <NoResultsPlaceholder query={query} clear={clear} />
            ) : null}
          </TitleSection>
        </PageCard>
      </StaticScreen>
    </Main>
  );
};

export default FaqPage;
